import { useState } from 'react';
import type {
  AdminAnalyticsReport,
  AttributionSummary,
  ExecutiveInsight,
  JourneySummary,
  NeedsAttention,
  OperationalDiagnosis,
  PriorityAction,
} from '../types';
import {
  Card,
  SectionLabel,
  EmptyState,
  SupportKpi,
  JourneyFunnel,
  AttributionPerformance,
  ExecutiveInsightsPanel,
  LeadJourney,
  ExportableReasonExplorers,
  STATUSES_WITH_REASONS,
  STATUS_COLORS,
  PALETTE,
  num,
  pct,
  pctNum,
} from './EodExecutiveReport';

// ============================================================================
// TYPES
// ============================================================================

type MorningChartData = {
  journey_summary?: JourneySummary;
  yesterday_activity?: {
    totalCalls: number;
    contactedYesterday: number;
    newLeadContacted: number;
    qualified: number;
    booked: number;
    won: number;
    lost: number;
  };
  attribution_summary?: AttributionSummary;
  executive_insights?: ExecutiveInsight[];
  needs_attention?: NeedsAttention;
  priority_actions?: PriorityAction[];
  operational_diagnosis?: OperationalDiagnosis[];
  social_activity?: {
    instagram_comments: number;
    facebook_comments?: number;
    instagram_messages: number;
    facebook_messages?: number;
    social_messages?: number;
    social_replies?: number;
    mentions?: number;
    reviews: number;
  };
  whatsapp_engagement?: {
    leads_received: number;
    called_by_team: number;
    reached_by_team: number;
    contact_coverage: number;
    patient_responded_back: number;
    organic: number;
    meta_ctwa: number;
    unique_whatsapp_patients_replied: number;
    whatsapp_replies: number;
  };
  source_funnels?: {
    whatsapp: { total: number; reached: number; qualified: number; positive: number };
    call: { total_signals: number; contacted: number; qualified: number; positive: number };
  };
  social_funnels?: {
    facebook: { messages_received: number; replied_by_team: number; leads_created: number; qualified: number; positive: number; appointments: number };
    instagram: { messages_received: number; replied_by_team: number; leads_created: number; qualified: number; positive: number; appointments: number };
  };
  brief_snapshot?: {
    queue?: { unassigned: number; followups: number; overdue: number; hot: number; warm: number };
  };
  whatsapp_brief?: string;
  cohort_date?: string;
  report_date?: string;
  status_reason_breakdown?: Array<{ status: string; total: number; reasons: Array<{ reason: string; count: number; percentage: number }> }>;
  pipeline?: Array<{ status: string; count: number }>;
  pipeline_total?: number;
};

// ============================================================================
// NEEDS ATTENTION SECTION
// ============================================================================

function NeedsAttentionCard({ needs, totalLeads }: { needs: NeedsAttention; totalLeads: number }) {
  const items = [
    { label: 'Yesterday leads still unassigned', value: needs.yesterday_unassigned, color: STATUS_COLORS.unassigned, priority: true },
    { label: 'Yesterday leads still not called', value: needs.yesterday_not_called, color: PALETTE.indigo, priority: true },
    { label: 'Called, never connected (incl. technical failures)', value: needs.yesterday_not_connected, color: PALETTE.blue },
    { label: 'Follow-ups due today', value: needs.followups_due_today, color: STATUS_COLORS.follow_up },
    { label: 'Overdue follow-ups', value: needs.followups_overdue, color: STATUS_COLORS.lost, priority: true },
    { label: 'AI-rated hot (tier 1) pending — not human-qualified', value: needs.hot_pending, color: STATUS_COLORS.qualified },
    { label: 'AI-rated warm (tier 2) pending', value: needs.warm_pending, color: PALETTE.violet },
  ].filter((item) => item.value > 0);

  if (items.length === 0) {
    return <EmptyState message="No pending actions — all yesterday's leads are handled." />;
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <div
          key={item.label}
          className={`rounded-xl border p-4 ${item.priority ? 'border-amber-200 bg-amber-50' : 'border-slate-100 bg-white'}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-medium text-[#17213A]">{item.label}</span>
            <span className="text-[20px] font-bold tabular-nums" style={{ color: item.color }}>
              {item.value}
            </span>
          </div>
          {totalLeads > 0 && item.value > 0 && (
            <div className="mt-1 text-[11px] text-[#8A9AB5]">
              {pct(item.value, totalLeads)} of yesterday's {totalLeads} leads
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// PRIORITY ACTIONS SECTION
// ============================================================================

function PriorityActionsList({ actions }: { actions: PriorityAction[] }) {
  if (!actions || actions.length === 0) {
    return <EmptyState message="No priority actions flagged for today." />;
  }

  const priorityColor: Record<string, string> = {
    high: 'bg-rose-100 text-rose-700 border-rose-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-slate-100 text-slate-600 border-slate-200',
  };

  return (
    <div className="space-y-2">
      {actions.map((action, i) => (
        <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3">
          <span className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${priorityColor[action.priority]}`}>
            {action.priority}
          </span>
          <span className="text-[13px] font-medium text-[#17213A]">{action.text}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// OPERATIONAL VS MARKETING DIAGNOSIS
// ============================================================================

function OperationalDiagnosisTable({ rows }: { rows: OperationalDiagnosis[] }) {
  if (!rows || rows.length === 0) {
    return <EmptyState message="No campaign diagnosis data available." />;
  }

  const verdictStyle: Record<string, { bg: string; text: string; label: string }> = {
    healthy: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Healthy' },
    operational_issue: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Operational Issue' },
    marketing_issue: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Marketing Issue' },
    closing_gap: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Closing Gap' },
    insufficient_data: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Insufficient Data' },
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
            <th className="px-2 py-2 font-medium">Campaign</th>
            <th className="px-2 py-2 text-right font-medium">Leads</th>
            <th className="px-2 py-2 text-right font-medium">Assigned</th>
            <th className="px-2 py-2 text-right font-medium">Called</th>
            <th className="px-2 py-2 text-right font-medium">Conn.</th>
            <th className="px-2 py-2 text-right font-medium" title="Current Won status; includes legacy wins without registration">Won (status)</th>
            <th className="px-2 py-2 font-medium">Verdict</th>
            <th className="px-2 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const style = verdictStyle[row.verdict] ?? verdictStyle.insufficient_data;
            return (
              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-2 py-2 font-medium text-[#17213A]">{row.campaign}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.total_leads}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.assigned}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.called}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.connected}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold text-emerald-600">{row.won}</td>
                <td className="px-2 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${style.bg} ${style.text}`}>
                    {style.label}
                  </span>
                </td>
                <td className="px-2 py-2 text-[11px] text-slate-600">{row.notes}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// WHATSAPP BRIEF SECTION
// ============================================================================

function WhatsAppBriefCard({ text, onRegenerate, regenerating }: { text: string; onRegenerate: () => void; regenerating: boolean }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available
    }
  };

  return (
    <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">WhatsApp Brief</p>
          <p className="mt-1 text-xs text-slate-600">Copy into the CRM / marketing WhatsApp group</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating}
            className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 disabled:opacity-50"
          >
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
          <button
            type="button"
            onClick={copy}
            disabled={regenerating}
            className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-xl border border-emerald-100 bg-white p-3 text-xs leading-5 text-slate-700">
        {text}
      </pre>
    </div>
  );
}

function EngagementCard({
  social,
  whatsapp,
  queue,
}: {
  social?: MorningChartData['social_activity'];
  whatsapp?: MorningChartData['whatsapp_engagement'];
  queue?: { unassigned: number; followups: number; overdue: number; hot: number; warm: number };
}) {
  if (!social && !whatsapp && !queue) return null;
  const items = [
    ['Instagram comments', social?.instagram_comments ?? 0],
    ['Facebook comments', social?.facebook_comments ?? 0],
    ['Instagram DMs', social?.instagram_messages ?? 0],
    ['Facebook DMs', social?.facebook_messages ?? 0],
    ['Social inbox messages', social?.social_messages ?? 0],
    ['Social replies by team', social?.social_replies ?? 0],
    ['Mentions', social?.mentions ?? 0],
    ['Reviews', social?.reviews ?? 0],
    ['Patient responses', whatsapp?.patient_responded_back ?? 0],
    ['Unique WhatsApp patients replied', whatsapp?.unique_whatsapp_patients_replied ?? 0],
    ['WhatsApp replies sent', whatsapp?.whatsapp_replies ?? 0],
    ['Unassigned', queue?.unassigned ?? 0],
    ['Follow-ups due', queue?.followups ?? 0],
    ['Overdue follow-ups', queue?.overdue ?? 0],
    ['AI-rated hot leads (tier 1, unassigned)', queue?.hot ?? 0],
    ['AI-rated warm leads (tier 2, unassigned)', queue?.warm ?? 0],
  ];
  return (
    <Card>
      <SectionLabel title="Social & WhatsApp Engagement" subtitle="Inbound activity from yesterday" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {items.map(([label, value]) => (
          <div key={String(label)} className="rounded-xl border border-slate-100 bg-white p-3">
            <div className="text-[11px] text-slate-500">{label}</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-[#17213A]">{value}</div>
          </div>
        ))}
      </div>
      {whatsapp && (
        <p className="mt-3 text-xs text-slate-600">
          WhatsApp organic {whatsapp.organic} · Meta CTWA {whatsapp.meta_ctwa}
        </p>
      )}
    </Card>
  );
}

function SourceFunnelCard({ label, totalLabel, total, contacted, qualified, positive }: { label: string; totalLabel: string; total: number; contacted: number; qualified: number; positive: number }) {
  return <div className="rounded-xl border border-slate-100 bg-white p-3">
    <div className="text-sm font-semibold text-[#17213A]">{label}</div>
    <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-slate-600">
      <span>{totalLabel}</span><strong className="text-right text-slate-900">{total}</strong>
      <span>{label === 'Call Signals' ? 'Connected (answered)' : 'Reached'}</span><strong className="text-right text-slate-900">{contacted}</strong>
      <span>Marked Qualified</span><strong className="text-right text-slate-900">{qualified}</strong>
      <span title="Qualification outcome recorded as positive">Positive (human-confirmed)</span><strong className="text-right text-slate-900">{positive}</strong>
    </div>
  </div>;
}

function SocialFunnelCard({ label, funnel }: { label: string; funnel: NonNullable<MorningChartData['social_funnels']>['facebook'] }) {
  return <div className="rounded-xl border border-slate-100 bg-white p-3">
    <div className="text-sm font-semibold text-[#17213A]">{label}</div>
    <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-slate-600">
      <span>Messages Received</span><strong className="text-right text-slate-900">{funnel.messages_received}</strong>
      <span>Replied by Team</span><strong className="text-right text-slate-900">{funnel.replied_by_team}</strong>
      <span>Leads Created</span><strong className="text-right text-slate-900">{funnel.leads_created}</strong>
      <span>Marked Qualified</span><strong className="text-right text-slate-900">{funnel.qualified}</strong>
      <span title="Qualification outcome recorded as positive">Positive (human-confirmed)</span><strong className="text-right text-slate-900">{funnel.positive}</strong>
      <span>Appointments</span><strong className="text-right text-slate-900">{funnel.appointments}</strong>
    </div>
  </div>;
}

function SourceFunnelCardSection({ funnels, socialFunnels }: { funnels?: MorningChartData['source_funnels']; socialFunnels?: MorningChartData['social_funnels'] }) {
  if (!funnels && !socialFunnels) return null;
  return <Card>
    <SectionLabel title="Digital and Social Performance" subtitle="Yesterday's source-specific activity and outcomes" />
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {funnels?.whatsapp && <SourceFunnelCard label="WhatsApp Leads" totalLabel="Total" total={funnels.whatsapp.total} contacted={funnels.whatsapp.reached} qualified={funnels.whatsapp.qualified} positive={funnels.whatsapp.positive} />}
      {socialFunnels?.facebook && <SocialFunnelCard label="Facebook Messages" funnel={socialFunnels.facebook} />}
      {socialFunnels?.instagram && <SocialFunnelCard label="Instagram Messages" funnel={socialFunnels.instagram} />}
      {funnels?.call && <SourceFunnelCard label="Call Signals" totalLabel="Patients called (outbound)" total={funnels.call.total_signals} contacted={funnels.call.contacted} qualified={funnels.call.qualified} positive={funnels.call.positive} />}
    </div>
  </Card>;
}

// ============================================================================
// MAIN MORNING MANAGEMENT REPORT
// ============================================================================

export default function MorningManagementReport({ report, onRegenerate, regenerating }: { report: AdminAnalyticsReport; onRegenerate: () => void; regenerating: boolean }) {
  const charts = (report.chart_data ?? {}) as MorningChartData;
  const summary = (report.overall_summary ?? {}) as Record<string, number>;

  const journey = charts.journey_summary;
  const activity = charts.yesterday_activity;
  const attribution = charts.attribution_summary;
  const insights = charts.executive_insights ?? [];
  const needs = charts.needs_attention;
  const priorityActions = charts.priority_actions ?? [];
  const operationalDiagnosis = charts.operational_diagnosis ?? [];
  const social = charts.social_activity;
  const whatsapp = charts.whatsapp_engagement;
  const sourceFunnels = charts.source_funnels;
  const socialFunnels = charts.social_funnels;
  const queue = charts.brief_snapshot?.queue;
  const whatsappBrief = charts.whatsapp_brief ?? '';
  const cohortDate = charts.cohort_date ?? '';
  const reportDate = charts.report_date ?? '';
  const statusReasonBreakdown = charts.status_reason_breakdown ?? [];
  const pipeline = charts.pipeline ?? [];
  const pipelineTotal = num(charts.pipeline_total ?? summary.total_new_leads ?? 0);

  const totalLeads = journey?.total_leads ?? pipelineTotal;

  // Default status selection for reason explorer.
  const [selectedStatus, setSelectedStatus] = useState<string>(() => {
    const lostEntry = pipeline.find((p) => p.status === 'lost' && p.count > 0);
    if (lostEntry) return 'lost';
    for (const s of STATUSES_WITH_REASONS) {
      const found = pipeline.find((p) => p.status === s && p.count > 0);
      if (found) return s;
    }
    const firstOutcome = pipeline.find((p) => p.status !== 'unassigned' && p.count > 0);
    return firstOutcome?.status ?? 'lost';
  });

  return (
    <div className="space-y-6 px-6 py-6 pb-10">
      {/* ===== COHORT LABEL ===== */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
        <p className="text-[13px] font-medium text-blue-800">
          Morning Management Report{reportDate ? ` — ${reportDate}` : ''}
        </p>
        <p className="mt-0.5 text-[12px] text-blue-600">
          Analyzing leads generated: <span className="font-semibold">{cohortDate || 'yesterday'}</span>
        </p>
      </div>

      {/* ===== 1. YESTERDAY OVERVIEW — KPI cards ===== */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SupportKpi
          label="Yesterday's Leads"
          value={totalLeads}
          sub={journey ? `${journey.assigned} assigned · ${journey.unassigned} unassigned` : ''}
          accent={PALETTE.indigo}
        />
        <SupportKpi
          label={activity ? 'Patients on Calls Yesterday' : 'Called'}
          value={activity?.contactedYesterday ?? journey?.called ?? 0}
          sub={activity ? `${activity.totalCalls} call attempt${activity.totalCalls === 1 ? '' : 's'} · any lead, inbound + outbound` : (journey ? `${journey.connected} connected · ${journey.not_connected} not connected` : '')}
          accent={PALETTE.blue}
        />
        <SupportKpi
          label="Marked Won Yesterday (status)"
          value={activity?.won ?? journey?.won ?? 0}
          sub={activity ? 'Any lead, not only yesterday\'s; not registrations' : (totalLeads > 0 ? `${pctNum(journey?.won ?? 0, totalLeads)}% of yesterday's leads` : '')}
          accent={STATUS_COLORS.won}
        />
        <SupportKpi
          label="Marked Lost Yesterday"
          value={activity?.lost ?? journey?.lost ?? 0}
          sub={activity ? 'Any lead, not only yesterday\'s' : (totalLeads > 0 ? `${pctNum(journey?.lost ?? 0, totalLeads)}% of yesterday's leads` : '')}
          accent={STATUS_COLORS.lost}
        />
      </div>

      {/* Secondary KPI row — Open + Qualified */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SupportKpi
          label="Still Open"
          value={journey?.still_open ?? 0}
          sub={totalLeads > 0 ? `${pctNum(journey?.still_open ?? 0, totalLeads)}% of yesterday's leads` : ''}
          accent={PALETTE.textSecondary}
        />
        <SupportKpi
          label="Marked Qualified Yesterday"
          value={activity?.qualified ?? journey?.qualified ?? 0}
          sub="Any lead, any qualification outcome (declined included)"
          accent={STATUS_COLORS.qualified}
        />
        <SupportKpi
          label="Not Called"
          value={journey?.not_called ?? 0}
          sub={totalLeads > 0 ? `${pctNum(journey?.not_called ?? 0, totalLeads)}% untouched` : ''}
          accent={STATUS_COLORS.unassigned}
        />
      </div>

      {/* ===== 2. YESTERDAY LEAD JOURNEY ===== */}
      {journey && journey.total_leads > 0 && (
        <Card>
          <SectionLabel
            title="Yesterday's Lead Journey"
            subtitle="Total → Assigned → Called → Connected → Marked Qualified → Positive → Won (status) → Registered"
            right={<span className="text-[13px] font-medium text-[#8A9AB5]">{journey.total_leads} leads</span>}
          />
          <JourneyFunnel journey={journey} />
        </Card>
      )}

      {/* ===== 3. NEEDS ATTENTION TODAY ===== */}
      {needs && (
        <Card>
          <SectionLabel
            title="Needs Attention Today"
            subtitle="Carry-forward items from yesterday's cohort"
          />
          <NeedsAttentionCard needs={needs} totalLeads={totalLeads} />
        </Card>
      )}

      {/* ===== 4. ATTRIBUTION PERFORMANCE ===== */}
      {attribution && (attribution.campaigns.length > 0 || attribution.adsets.length > 0 || attribution.ads.length > 0) && (
        <Card>
          <SectionLabel
            title="Attribution Performance"
            subtitle="Source → Campaign → Ad Set → Ad breakdown for yesterday's leads"
          />
          <AttributionPerformance attribution={attribution} />
        </Card>
      )}

      {/* ===== 5. OUTCOME / REASON ANALYSIS ===== */}
      {pipeline.length > 0 && (
        <Card>
          <SectionLabel
            title="Outcome & Reason Analysis"
            subtitle="Click a status to see why leads converted or rejected"
            right={<span className="text-[13px] font-medium text-[#8A9AB5]">{pipelineTotal} leads</span>}
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
            <LeadJourney
              pipeline={pipeline}
              pipelineTotal={pipelineTotal}
              selected={selectedStatus}
              onSelect={setSelectedStatus}
            />
            <ExportableReasonExplorers
              selectedStatus={selectedStatus}
              pipeline={pipeline}
              pipelineTotal={pipelineTotal}
              statusReasonBreakdown={statusReasonBreakdown}
            />
          </div>
        </Card>
      )}

      {/* ===== 6. OPERATIONAL VS MARKETING DIAGNOSIS ===== */}
      {operationalDiagnosis.length > 0 && (
        <Card>
          <SectionLabel
            title="Operational vs Marketing Diagnosis"
            subtitle="Distinguish poor follow-up from poor campaign quality"
          />
          <OperationalDiagnosisTable rows={operationalDiagnosis} />
        </Card>
      )}

      <SourceFunnelCardSection funnels={sourceFunnels} socialFunnels={socialFunnels} />
      <EngagementCard social={social} whatsapp={whatsapp} queue={queue} />

      {/* ===== 7. PRIORITY INSIGHTS ===== */}
      <div className="space-y-4">
        <div className="px-1">
          <h3 className="text-[17px] font-semibold tracking-tight text-[#17213A]">Priority Insights</h3>
          <p className="mt-0.5 text-[13px] text-[#8A9AB5]">Automated findings from yesterday's attribution data</p>
        </div>
        {insights.length > 0 && <ExecutiveInsightsPanel insights={insights} />}
        <Card>
          <SectionLabel title="Priority Actions" subtitle="Deterministic actions from actual metrics" />
          <PriorityActionsList actions={priorityActions} />
        </Card>
      </div>

      {/* ===== 8. WHATSAPP BRIEF ===== */}
      {whatsappBrief && <WhatsAppBriefCard text={whatsappBrief} onRegenerate={onRegenerate} regenerating={regenerating} />}
    </div>
  );
}
